import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ChevronDown, ChevronRight, Calculator, Percent, TrendingUp, TrendingDown, Package, PiggyBank, Layers, Wallet, CheckSquare, Target, User, Settings, AlertCircle, Box, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Icon from './AppIcon';
import { whiteLabelService } from '../services/whiteLabelService';


const Sidebar = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState({
    overview: true,
    mainCalculators: true,
    calculators: false, // Changed from true to false - Extra Calculators collapsed by default
    admin: true
  });
  const [branding, setBranding] = useState(null);
  const [isLoadingBranding, setIsLoadingBranding] = useState(true);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      setIsLoadingBranding(true);
      const settings = await whiteLabelService?.getSettings();
      setBranding(settings);
    } catch (error) {
      console.error('Error loading branding:', error);
      // If error, branding will remain null and defaults will be used
    } finally {
      setIsLoadingBranding(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleProfileClick = () => {
    navigate('/business-settings-configuration');
  };

  const handleSupportClick = () => {
    navigate('/support-tickets');
  };

  const navigationSections = [
    {
      id: 'overview',
      title: 'OVERVIEW',
      items: [
        { path: '/', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/project-management-dashboard', label: 'Project Management', icon: CheckSquare },
        { path: '/business-budget-manager-analytics-dashboard', label: 'Budget Manager', icon: Wallet },
        { path: '/asset-management-overview', label: 'Asset Manager', icon: Box },
        { path: '/sales-performance-tracker', label: 'Sales Tracker', icon: BarChart3 }
      ]
    },
    {
      id: 'mainCalculators',
      title: 'MAIN CALCULATORS',
      items: [
        { path: '/ecommerce-roi-calculator', label: 'ECOM ROI Calculator', icon: Calculator },
        { path: '/lead-roi-calculator-analysis-engine', label: 'LEAD ROI Calculator', icon: Target }
      ]
    },
    {
      id: 'calculators',
      title: 'EXTRA CALCULATORS',
      items: [
        { path: '/vat-tax-calculator', label: 'VAT/Tax Calculator', icon: Percent },
        { path: '/pricing-margin-calculator', label: 'Pricing & Margin', icon: TrendingUp },
        { path: '/bundle-pricing-calculator', label: 'Bundle Pricing', icon: Layers },
        { path: '/cash-flow-forecast-calculator', label: 'Cash Flow Forecast', icon: TrendingDown },
        { path: '/promo-discount-impact-calculator', label: 'Promo Discount Impact', icon: Package },
        { path: '/cogs-percentage-calculator', label: 'COGS Percentage', icon: PiggyBank }
      ]
    }
  ];

  // Remove admin section - admin users will use separate AdminSidebar

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-gradient-to-b from-blue-700 via-blue-600 to-blue-800 text-white shadow-2xl z-60 overflow-y-auto flex flex-col">
      {/* Logo Section */}
      <div className="p-6 border-b border-blue-500/30">
        {isLoadingBranding ? (
          <div className="animate-pulse">
            <div className="h-8 bg-blue-500/30 rounded mb-2"></div>
            <div className="h-4 bg-blue-500/20 rounded w-3/4"></div>
          </div>
        ) : branding?.logoUrl ? (
          <div className="flex items-center justify-center">
            <img
              src={branding?.logoUrl}
              alt={branding?.brandName || 'Brand Logo'}
              className="max-h-16 w-auto object-contain"
            />
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-white mb-1">
              {branding?.brandName || 'FinanceFlow'}
            </h1>
            <p className="text-xs text-blue-100">
              {branding?.slogan || 'Professional Dashboard'}
            </p>
          </>
        )}
      </div>

      {/* Navigation Sections */}
      <nav className="py-4 flex-1">
        {navigationSections?.map((section) => (
          <div key={section?.id} className="mb-2">
            <button
              onClick={() => toggleSection(section?.id)}
              className="w-full px-6 py-3 flex items-center justify-between text-blue-100 hover:text-white text-xs font-bold tracking-wide transition-colors"
            >
              <span>{section?.title}</span>
              {openSections?.[section?.id] ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>

            {openSections?.[section?.id] && (
              <div className="space-y-1 px-3">
                {section?.items?.map((item) => {
                  const Icon = item?.icon;
                  return (
                    <NavLink
                      key={item?.path}
                      to={item?.path}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-blue-500/50 text-white shadow-lg'
                            : 'text-blue-50 hover:bg-blue-600/30 hover:text-white'
                        }`
                      }
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="truncate">{item?.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Support Button - Compact Size */}
      <div className="px-4 pb-3">
        <button
          onClick={handleSupportClick}
          className="w-full bg-gradient-to-r from-white to-blue-50 text-blue-700 px-3 py-2 rounded-lg font-medium hover:from-blue-50 hover:to-white hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-sm border border-blue-100/50 hover:scale-[1.01] active:scale-[0.99]"
        >
          <AlertCircle className="w-4 h-4" />
          <span className="text-xs">Report a Bug / Support</span>
        </button>
      </div>

      {/* User Profile Section at Bottom */}
      <div className="border-t border-blue-500/30 p-4">
        <button
          onClick={handleProfileClick}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-blue-50 hover:bg-blue-600/30 hover:text-white transition-all duration-200"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
            {profile?.fullName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 text-left overflow-hidden">
            <div className="text-sm font-semibold text-white truncate">
              {profile?.fullName || user?.email?.split('@')?.[0] || 'User'}
            </div>
            <div className="text-xs text-blue-200 flex items-center gap-1">
              <Settings className="w-3 h-3" />
              <span>Account Info</span>
            </div>
          </div>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;